import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/support`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching support messages:', error);
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (messageId) => {
    try {
      await axios.put(`${API}/support/${messageId}/resolve`);
      toast.success('Mensaje marcado como resuelto');
      fetchMessages();
    } catch (error) {
      console.error('Error resolving message:', error);
      toast.error('Error al resolver mensaje');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('¿Está seguro de eliminar este mensaje?')) {
      return;
    }

    try {
      await axios.delete(`${API}/support/${messageId}`);
      toast.success('Mensaje eliminado');
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Error al eliminar mensaje');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    return msg.status === filter;
  });

  const stats = {
    total: messages.length,
    pending: messages.filter(m => m.status === 'pending').length,
    resolved: messages.filter(m => m.status === 'resolved').length
  };

  if (loading) {
    return <div className="loading">Cargando mensajes...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Mensajes de Soporte</h1>
        <Link to="/" className="btn-back">
          ← Volver
        </Link>
      </div>

      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.resolved}</div>
          <div className="stat-label">Resueltos</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="status-filter">Filtrar por estado:</label>
          <select
            id="status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="resolved">Resueltos</option>
          </select>
        </div>
      </div>

      {filteredMessages.length === 0 ? (
        <div className="empty-state">
          <p>No hay mensajes de soporte</p>
        </div>
      ) : (
        <div className="messages-grid">
          {filteredMessages.map((message) => (
            <div
              key={message.id}
              className={`message-card ${message.status === 'resolved' ? 'resolved' : 'pending'}`}
            >
              <div className="message-header">
                <div className="message-user">
                  <h3>{message.name}</h3>
                  <span className="user-email">{message.email}</span>
                </div>
                <span className={`message-status status-${message.status}`}>
                  {message.status === 'pending' ? '⏳ Pendiente' : '✅ Resuelto'}
                </span>
              </div>

              <div className="message-subject">
                <strong>Asunto:</strong> {message.subject}
              </div>

              <div className="message-body">
                {message.message}
              </div>

              <div className="message-meta">
                <div className="meta-info">
                  <span>📅 {formatDate(message.created_at)}</span>
                  {message.submitted_by && (
                    <span>👤 {message.submitted_by} ({message.user_role})</span>
                  )}
                </div>
                {message.resolved_at && (
                  <div className="resolved-info">
                    <span>Resuelto por {message.resolved_by} el {formatDate(message.resolved_at)}</span>
                  </div>
                )}
              </div>

              {user?.role === 'admin' && (
                <div className="message-actions">
                  {message.status === 'pending' && (
                    <button
                      className="btn-resolve"
                      onClick={() => handleResolve(message.id)}
                    >
                      Marcar como Resuelto
                    </button>
                  )}
                  <button
                    className="btn-delete-message"
                    onClick={() => handleDelete(message.id)}
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportMessages;
